import { ApiError, NetworkError } from "./errors.js";
/**
 * createTransport builds the one object the generated operations run on.
 *
 * `credentials: "include"` is not optional here: the entire customer surface
 * is authenticated by the HttpOnly SSO cookie, and a fetch that omits
 * credentials succeeds at the network layer and fails at every route, which
 * looks exactly like a broken login.
 */
export function createTransport(options = {}) {
    const baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
    const doFetch = options.fetch ?? globalThis.fetch;
    return {
        async request(spec) {
            const url = baseUrl + spec.path + encodeQuery(spec.query);
            const headers = new Headers({ accept: "application/json" });
            for (const [k, v] of Object.entries(options.headers ?? {})) {
                headers.set(k, v);
            }
            for (const [k, v] of Object.entries(spec.headers ?? {})) {
                if (v !== undefined) {
                    headers.set(k, v);
                }
            }
            const init = {
                method: spec.method,
                headers,
                credentials: "include",
            };
            if (spec.signal) {
                init.signal = spec.signal;
            }
            if (spec.body !== undefined) {
                headers.set("content-type", "application/json");
                init.body = JSON.stringify(spec.body);
            }
            let response;
            try {
                response = await doFetch(url, init);
            }
            catch (cause) {
                throw new NetworkError(`${spec.operationId}: the platform could not be reached.`, cause);
            }
            if (!response.ok) {
                const problem = await readProblem(response);
                if (response.status === 401) {
                    options.onUnauthenticated?.(spec);
                }
                throw new ApiError(response.status, problem, `${spec.operationId} failed with ${response.status}.`);
            }
            // 204 and an empty 200 both mean "nothing to read". Parsing them would
            // throw on the empty string, so the generated void operations would
            // fail on success.
            if (response.status === 204 || response.headers.get("content-length") === "0") {
                return undefined;
            }
            const text = await response.text();
            if (text === "") {
                return undefined;
            }
            return JSON.parse(text);
        },
    };
}
/**
 * readProblem parses the error body, tolerating one that is not a problem
 * document. A gateway between the browser and the platform can answer with
 * HTML, and a client that throws while parsing the error loses the status
 * code — the only thing it actually knew.
 */
async function readProblem(response) {
    try {
        const text = await response.text();
        if (text === "") {
            return undefined;
        }
        const parsed = JSON.parse(text);
        if (typeof parsed === "object" && parsed !== null && "code" in parsed) {
            return parsed;
        }
        return undefined;
    }
    catch {
        return undefined;
    }
}
/**
 * encodeQuery drops undefined, null and empty-string values rather than
 * sending them. `?currency=` is not the same request as no currency at all:
 * the platform parses the empty value and answers 400.
 */
function encodeQuery(query) {
    if (!query) {
        return "";
    }
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null || value === "") {
            continue;
        }
        params.set(key, String(value));
    }
    const encoded = params.toString();
    return encoded === "" ? "" : "?" + encoded;
}
//# sourceMappingURL=transport.js.map